import { Link, Navigate, useParams } from "react-router"
import { ArrowLeft, Copy } from "lucide-react"
import { useState } from "react"

import { useArticleArchiveItem } from "@/api/hooks/articleGenerator"
import { useAuth } from "@/auth/AuthProvider"

async function copyRichHtml(html: string, plain: string) {
  try {
    if (typeof ClipboardItem !== "undefined" && navigator.clipboard.write) {
      const item = new ClipboardItem({
        "text/html": new Blob([html], { type: "text/html" }),
        "text/plain": new Blob([plain], { type: "text/plain" }),
      })
      await navigator.clipboard.write([item])
      return
    }
  } catch {
    // fall through
  }
  const holder = document.createElement("div")
  holder.contentEditable = "true"
  holder.innerHTML = html
  holder.style.position = "fixed"
  holder.style.left = "-9999px"
  document.body.appendChild(holder)
  const range = document.createRange()
  range.selectNodeContents(holder)
  const sel = window.getSelection()
  sel?.removeAllRanges()
  sel?.addRange(range)
  try {
    document.execCommand("copy")
  } finally {
    sel?.removeAllRanges()
    document.body.removeChild(holder)
  }
}

function escapeHtml(text: string): string {
  return text
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
}

function stripHtml(html: string): string {
  const el = document.createElement("div")
  el.innerHTML = html
  return (el.textContent || el.innerText || "").replace(/\n{3,}/g, "\n\n").trim()
}

export default function ArticleArchiveDetailTab() {
  const { canUseArticleGenerator } = useAuth()
  const params = useParams()
  const id = Number(params.id)
  const detail = useArticleArchiveItem(Number.isFinite(id) ? id : null)
  const [copyState, setCopyState] = useState<"idle" | "body" | string>("idle")

  if (!canUseArticleGenerator) {
    return <Navigate to="/tools/url-encoder" replace />
  }

  if (!Number.isFinite(id) || id <= 0) {
    return <Navigate to="/tools/article-generator/archive" replace />
  }

  const article = detail.data

  async function handleCopyTitle(label: string) {
    await copyRichHtml(`<p>${escapeHtml(label)}</p>`, label)
    setCopyState(`title:${label}`)
    setTimeout(() => setCopyState("idle"), 2000)
  }

  async function handleCopyBody() {
    if (!article) return
    await copyRichHtml(article.body_html, stripHtml(article.body_html))
    setCopyState("body")
    setTimeout(() => setCopyState("idle"), 2000)
  }

  return (
    <div className="mx-auto w-full max-w-6xl space-y-4">
      <Link
        to="/tools/article-generator/archive"
        className="inline-flex items-center gap-1.5 text-sm text-[#8b9aff] hover:underline"
      >
        <ArrowLeft className="h-4 w-4" aria-hidden />
        Back to archive
      </Link>

      {detail.isLoading ? <p className="text-sm text-[#aaa]">Loading…</p> : null}
      {detail.isError ? (
        <p className="rounded border border-red-500/40 bg-red-950/40 px-3 py-2 text-sm text-red-200">
          Article not found
        </p>
      ) : null}

      {article ? (
        <div className="space-y-4">
          <p className="text-xs text-[#aaa]">
            {article.word_count} words · {article.h2_count} H2 · {article.provider} ·{" "}
            {article.language} · {article.countries.join(", ")}
          </p>

          {article.related_searches.length > 0 ? (
            <div>
              <p className="mb-2 text-sm text-[#aaa]">Related searches</p>
              <div className="flex flex-wrap gap-2">
                {article.related_searches.map((label) => (
                  <span
                    key={label}
                    className="inline-flex items-center gap-1.5 rounded-md border border-[#555] bg-[#2a2a2a] py-1 pl-3 pr-1.5 text-sm text-white"
                  >
                    <span>{label}</span>
                  </span>
                ))}
              </div>
            </div>
          ) : null}

          <fieldset className="rounded border border-[#555] bg-[#252525] px-4 pb-3 pt-1">
            <legend className="px-1 text-sm text-[#aaa]">Article titles</legend>
            <div className="space-y-2">
              {(article.titles?.length ? article.titles : [article.title]).map(
                (option, index) => (
                  <div
                    key={`${index}-${option}`}
                    className="flex items-start gap-3 rounded-md border border-[#555] bg-[#2a2a2a] px-3 py-2.5"
                  >
                    <p className="min-w-0 flex-1 text-[15px] font-semibold leading-snug text-white">
                      {option}
                    </p>
                    <button
                      type="button"
                      aria-label={`Copy title ${index + 1}`}
                      onClick={() => void handleCopyTitle(option)}
                      className="inline-flex shrink-0 items-center gap-1.5 rounded border border-[#555] bg-[#3a3a3a] px-3 py-1.5 text-sm text-white hover:bg-[#444]"
                    >
                      <Copy className="h-3.5 w-3.5" aria-hidden />
                      {copyState === `title:${option}` ? "Copied" : "Copy"}
                    </button>
                  </div>
                ),
              )}
            </div>
          </fieldset>

          <fieldset className="rounded border border-[#555] bg-[#252525] px-4 pb-4 pt-1">
            <legend className="px-1 text-sm text-[#aaa]">Article</legend>
            <div className="mb-3 flex justify-end">
              <button
                type="button"
                onClick={() => void handleCopyBody()}
                className="inline-flex items-center gap-1.5 rounded border border-[#555] bg-[#3a3a3a] px-3 py-1.5 text-sm text-white hover:bg-[#444]"
              >
                <Copy className="h-3.5 w-3.5" aria-hidden />
                {copyState === "body" ? "Copied" : "Copy"}
              </button>
            </div>
            <div
              className="space-y-3 text-[15px] leading-relaxed text-[#ddd] [&_a]:text-[#8b9aff] [&_a]:underline [&_h2]:mt-6 [&_h2]:text-xl [&_h2]:font-semibold [&_h2]:text-white [&_li]:ml-4 [&_ul]:list-disc [&_ul]:space-y-1"
              dangerouslySetInnerHTML={{ __html: article.body_html }}
            />
          </fieldset>
        </div>
      ) : null}
    </div>
  )
}
